/**
 * Страница установки Android-приложения (ROADMAP приложения, A1.7).
 *
 * Магазина нет (решение 2): APK лежит на нашем сервере, а эта страница —
 * ссылка на скачивание, QR-код для телефона и инструкция по установке
 * файла не из магазина. Гостевая: на неё ведёт письмо-приглашение,
 * а войти человек ещё не успел.
 */

import { APP_RELEASE_PATH, type AppRelease, parseAppRelease } from "@taskflow/client-core";
import qrcode from "qrcode-generator";
import { Download } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { BASE_PATH } from "@/shared/api/basePath";
import { SkeletonRows } from "@/shared/ui";

type State =
  | { kind: "loading" }
  | { kind: "missing" }
  | { kind: "error" }
  | { kind: "ready"; release: AppRelease };

const RELEASE_URL = `${BASE_PATH}${APP_RELEASE_PATH}`;

function formatSize(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} МБ`;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("ru-RU");
}

function QrCode({ url }: { url: string }) {
  const image = useMemo(() => {
    const code = qrcode(0, "M");
    code.addData(url);
    code.make();
    return code.createDataURL(5, 2);
  }, [url]);
  return (
    <img
      src={image}
      alt="QR-код ссылки на APK: наведите камеру телефона"
      className="size-40 rounded-md border border-line bg-white p-1"
    />
  );
}

export function AppDownloadPage() {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void fetch(RELEASE_URL, { cache: "no-cache" })
      .then(async (response) => {
        if (response.status === 404) return { kind: "missing" } as const;
        if (!response.ok) return { kind: "error" } as const;
        const release = parseAppRelease(await response.json());
        return release === null ? ({ kind: "error" } as const) : ({ kind: "ready", release } as const);
      })
      .catch(() => ({ kind: "error" }) as const)
      .then((next) => {
        if (!cancelled) setState(next);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const apkUrl =
    state.kind === "ready"
      ? new URL(`${BASE_PATH}/app/android/${state.release.file}`, window.location.origin).href
      : null;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-5">
      <main className="w-full max-w-[520px] rounded-lg border border-line bg-surface p-8 shadow-sm max-sm:p-5">
        <h1 className="text-[19px] font-extrabold tracking-tight text-fg">mytaskflow для Android</h1>
        <p className="mt-1 text-[13px] text-muted">
          Задачи, комментарии и уведомления на телефоне. Android 8.0 и новее.
        </p>

        {state.kind === "loading" && <SkeletonRows rows={3} className="mt-5" />}

        {state.kind === "missing" && (
          <p role="status" className="mt-5 rounded-md bg-info-soft px-3 py-2 text-[13px] text-info">
            Приложение ещё не опубликовано. Загляните сюда позже.
          </p>
        )}

        {state.kind === "error" && (
          <p role="alert" className="mt-5 rounded-md bg-danger-soft px-3 py-2 text-[13px] text-danger">
            Не удалось узнать текущую версию. Обновите страницу.
          </p>
        )}

        {state.kind === "ready" && apkUrl !== null && (
          <>
            <div className="mt-5 flex flex-wrap items-center gap-5">
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <p className="text-[13px] text-fg">
                  Версия <b>{state.release.versionName}</b>
                  <span className="text-muted">
                    {" "}
                    · {formatSize(state.release.size)}
                    {formatDate(state.release.releasedAt) !== "" &&
                      ` · ${formatDate(state.release.releasedAt)}`}
                  </span>
                </p>
                {state.release.notes !== "" && (
                  <p className="text-[13px] text-muted">{state.release.notes}</p>
                )}
                <a
                  href={apkUrl}
                  download={state.release.file}
                  className="mt-1 inline-flex h-9 w-fit items-center gap-2 rounded-md bg-accent px-4 text-sm font-semibold text-accent-fg hover:bg-accent-hover active:bg-accent-active"
                >
                  <Download className="size-4" aria-hidden />
                  Скачать APK
                </a>
              </div>
              <QrCode url={apkUrl} />
            </div>

            <h2 className="mt-6 text-[13px] font-extrabold text-fg">Как установить</h2>
            <ol className="mt-2 flex list-decimal flex-col gap-1.5 pl-5 text-[13px] text-fg">
              <li>Откройте эту страницу на телефоне или наведите камеру на QR-код.</li>
              <li>Скачайте файл и откройте его из уведомления о загрузке.</li>
              <li>
                Телефон попросит разрешить установку из браузера — разрешите: приложения нет
                в магазине, оно скачивается с нашего сервера.
              </li>
              <li>
                Если Google Play Защита предупредит о незнакомом разработчике, нажмите
                «Всё равно установить».
              </li>
              <li>Войдите с той же почтой и паролем, что и на сайте.</li>
            </ol>
            <p className="mt-3 text-[12px] text-muted">
              Новые версии приложение предложит установить само.
            </p>
          </>
        )}

        <Link to="/" className="mt-6 inline-block text-[13px] font-semibold text-accent hover:underline">
          Вернуться в задачник
        </Link>
      </main>
    </div>
  );
}
